// Gedeeld type voor site-instellingen — gebruikt door zowel server als client
export interface SiteSettings {
  // Topbar aankondigingsbalk (roterende berichten)
  topbarMessages: string[];

  // Homepage hero-sectie
  heroTitle: string;
  heroSubtitle: string;
  heroCtaDames: string;
  heroCtaHeren: string;
  heroImageDesktop?: string;  // liggend 2:1, getoond vanaf 769px; leeg = /images/hero-bg.webp
  heroImageMobile?: string;   // staand 4:5, getoond t/m 768px; leeg = /images/hero-bg-mobiel.webp

  // USP-blok (4 icoon + tekst blokken)
  usp1Title: string; usp1Text: string;
  usp2Title: string; usp2Text: string;
  usp3Title: string; usp3Text: string;
  usp4Title: string; usp4Text: string;

  // Bezorgen & Retourneren
  shippingFreeFrom: string;       // bijv. "€50"
  shippingCost: string;           // bijv. "€3,95"
  shippingCutoff: string;         // bijv. "16:00 uur"
  shippingCarrier: string;        // bijv. "PostNL"
  returnDays: string;             // bijv. "30"
  returnConditions: string;       // vrije tekst
  hygieneNote: string;

  // Maattabel drawer
  sizeChartIntro: string;
  sizeChartMeasureTitle: string;
  sizeChartStep1: string;
  sizeChartStep2: string;
  sizeChartStep3: string;
  sizeChartTip: string;

  // LogicParcel verzendinstelling
  parcelletterMaxKg: number;   // NL: onder dit gewicht → Parcelletter (L), erboven → Home Delivery (A)
  itemWeightKg: number;        // standaard gewicht per item (kg) als product geen gewicht heeft
  returnServiceCode: string;   // GLS retourcode: A = omweg naar winkeladres, H = Return, H1 = Pick Up-Return, C = Parcel.Shop Return
  // Retour-/winkeladres (gebruikt als ontvanger bij de A-omweg en als afzender op retourlabels)
  returnAddressName: string;
  returnStreet: string;
  returnHouseNumber: string;
  returnPostalCode: string;
  returnCity: string;
  returnCountry: string;

  // Footer
  footerTagline: string;
  footerEmail: string;
  footerPhone: string;
  footerAddress: string;
  footerKvk: string;
  footerBtw: string;       // BTW-identificatienummer (verplicht op factuur)
  footerIban: string;      // IBAN voor bankgegevens op factuur
  footerStoreName: string; // Officiële bedrijfsnaam (voor factuurkop)
  footerUsp1: string;
  footerUsp2: string;
  footerUsp3: string;
  aboutUsHtml?: string;
  faqJson?: string;

  // Chatbot instellingen
  chatbotEnabled: boolean;

  // Aanpasbare pop-up (nieuwsbrief / actie / kortingscode / mededeling) — punt 15
  popupEnabled: boolean;
  popupStartDate?: string;     // ISO-datum, leeg = geen startbeperking
  popupEndDate?: string;       // ISO-datum, leeg = geen eindbeperking (t/m einde van die dag)
  popupDelaySeconds: number;   // tonen na x seconden
  popupTitle: string;
  popupText: string;
  popupButtonLabel: string;    // leeg = geen knop
  popupButtonLink: string;
  popupImage?: string;         // leeg = geen afbeelding, tekst-gecentreerde opmaak
}

export const DEFAULT_SETTINGS: SiteSettings = {
  topbarMessages: [
    "Voor 16:00 uur besteld, vandaag verzonden",
    "Gratis verzending in NL vanaf €70",
    "14 dagen retourrecht",
    "Veilig betalen met iDEAL, Klarna & creditcard",
    "Specialist in ondergoed sinds jaar en dag",
  ],

  heroTitle: "Ondergoed en nachtmode voor het hele gezin",
  heroSubtitle: "Comfortabel, betrouwbaar en overzichtelijk. Van dames- en herenondergoed to thermo en sokken – alles wat je onder je outfit draagt.",
  heroCtaDames: "Shop dames",
  heroCtaHeren: "Shop heren",
  heroImageDesktop: "",
  heroImageMobile: "",

  usp1Title: "Gratis verzending", usp1Text: "Vanaf €70 binnen NL",
  usp2Title: "Snelle levering",   usp2Text: "Voor 16:00 besteld - Vandaag verzonden",
  usp3Title: "Vertrouwde merken", usp3Text: "Jarenlange ervaring",
  usp4Title: "Veilig betalen",    usp4Text: "iDEAL, Klarna & meer",

  parcelletterMaxKg: 1,
  itemWeightKg: 0.3,
  returnServiceCode: "A",
  returnAddressName: "Mijnondergoedwinkel.nl",
  returnStreet: "De Strookappe",
  returnHouseNumber: "20",
  returnPostalCode: "7491 JB",
  returnCity: "Delden",
  returnCountry: "NL",

  shippingFreeFrom: "€70",
  shippingCost: "€5,95",
  // Gelijk aan de live waarde in siteSettings/main én aan AV art. 5,
  // /bezorgen-retourneren, de FAQ en de badge op de productpagina. Stond tot
  // 19-9-2026 op 17:00 — alleen als standaardwaarde, live nooit gebruikt, maar
  // wel misleidend bij het lezen van de code.
  shippingCutoff: "16:00 uur",
  shippingCarrier: "PostNL",
  returnDays: "14",
  returnConditions: "Retourneren kan binnen 14 dagen, op eigen kosten. Artikelen moeten ongedragen, ongewassen en in originele verpakking zijn. Retour aanmelden via je account of via onze contactpagina. Na ontvangst en keuring storten wij het bedrag binnen 5 werkdagen terug.",
  hygieneNote: "Let op: om hygiënische redenen kunnen ondergoed en badmode niet worden geretourneerd als de hygiënesticker is verwijderd.",

  sizeChartIntro: "We helpen je via deze maattabel met het vinden van jouw perfecte maat. De maten geven een indicatie — twijfels? Neem gerust contact op.",
  sizeChartMeasureTitle: "Meten is weten!",
  sizeChartStep1: "Tailleomvang — Meet rondom het smalste deel van je taille, tussen je borstkas en heup.",
  sizeChartStep2: "Heupomvang — Meet rondom het breedste deel van je heupen en billen.",
  sizeChartStep3: "Borstomvang — Meet rondom het breedste deel van je borstkas.",
  sizeChartTip: "Twijfel je tussen twee maten? Kies dan de grotere maat voor extra comfort.",

  footerTagline: "Kwaliteitsondergoed voor het hele gezin",
  footerEmail: "info@mijnondergoedwinkel.nl",
  footerPhone: "",
  footerAddress: "",
  footerKvk: "",
  footerBtw: "",
  footerIban: "",
  footerStoreName: "Mijnondergoedwinkel.nl",
  footerUsp1: "Gratis verzending vanaf €70",
  footerUsp2: "14 dagen retourrecht",
  footerUsp3: "Veilig betalen met iDEAL & Klarna",
  aboutUsHtml: "",
  faqJson: "",

  chatbotEnabled: false, // nog niet live; ook als fallback-default zolang de instellingen nog laden

  popupEnabled: false,
  popupStartDate: "",
  popupEndDate: "",
  popupDelaySeconds: 5,
  popupTitle: "Welkom bij Mijnondergoedwinkel.nl",
  popupText: "Schrijf je in voor onze nieuwsbrief en ontvang de laatste acties en kortingscodes.",
  popupButtonLabel: "",
  popupButtonLink: "",
  popupImage: "",
};
